import { Tag } from "@/store/tags/tags-slice";
import { NoTranslate, Option } from "@faro-lotv/flat-ui";
import { assert } from "@faro-lotv/foundation";
import { ProjectExtensionItem } from "@faro-lotv/service-wires";

/** Defines a tag type that has reference to their parent tag */
export type TagWithParent = Tag & {
  /** The name of the parent tag. When null or undefined, it means that the tag has no parent. */
  parentTagName?: string | null;
  /** Whether the tag is disabled */
  disabled: boolean;
};

/**
 * Converts a `ProjectExtensionItem` object into an `Option` object for use in UI components.
 *
 * @param item - The project extension item containing option data.
 * @param isHeader - A boolean indicating if the option is a header.
 * @returns An `Option` object.
 */
function getOption(item: OptionEntry, isHeader: boolean): Option {
  return {
    key: item.id,
    value: item.id,
    label: <NoTranslate>{item.name}</NoTranslate>,
    isDisabled: item.isDisabled,
    isHeader,
  };
}

/** Defines the supported types by the class OptionEntry */
type SupportedTypes = ProjectExtensionItem | TagWithParent;

/**
 * Class representing an entry for an option in a selection component.
 * It encapsulates the properties of a project extension item or a tag with a parent.
 */
export class OptionEntry {
  id: string;
  value: string;
  name: string;
  isDisabled: boolean;
  parentId?: string | null;

  /**
   * Constructs an instance by extracting relevant properties from the provided item.
   *
   * @param item - The source object containing data to put in the option.
   * Throws an assertion error if the type of the item is not supported.
   */
  constructor(item: SupportedTypes) {
    this.id = item.id;
    this.value = item.id;
    this.name = item.name;
    if ("parentTagName" in item) {
      this.parentId = item.parentTagName;
      this.isDisabled = item.disabled;
    } else if ("parent_id" in item) {
      this.parentId = item.parent_id;
      this.isDisabled = item.readonly;
    } else {
      assert(false, "Unknown type of item");
    }
  }
}

/**
 * Defines a nested option entry that can contain child options.
 */
type NestedOptionEntry = {
  value: OptionEntry;
  children?: OptionEntry[];
};

/**
 * Generates an array of option objects for use in selection (dropdown) components.
 *
 * @param data - The project user extension items used to generate option values.
 * Notes: Items with a parent_id are considered as part of a group, and the group item itself is considered as a header.
 * Only one level of grouping is supported.
 * @returns An array of `Option` objects with groups defined as headers.
 */
export function getOptionValues(data: OptionEntry[]): Option[] {
  // We doing a first pass to group the items
  const notYetCreatedGroups = new Map<string, OptionEntry[]>();
  const createdGroups = new Map<string, NestedOptionEntry>();
  const allFirstLevelItems: OptionEntry[] = [];
  data.forEach((item) => {
    if (item.parentId) {
      // It's an item inside a group
      const group = createdGroups.get(item.parentId);
      if (group) {
        if (group.children === undefined) {
          group.children = [];
        }
        group.children.push(item);
      } else {
        let notYetCreatedGroup = notYetCreatedGroups.get(item.parentId);
        if (!notYetCreatedGroup) {
          notYetCreatedGroup = [];
          notYetCreatedGroups.set(item.parentId, notYetCreatedGroup);
        }
        notYetCreatedGroup.push(item);
      }
    } else {
      // It's a group item or an item without a group
      const group = notYetCreatedGroups.get(item.id);
      createdGroups.set(item.id, {
        value: item,
        children: group,
      });
      if (group) {
        notYetCreatedGroups.delete(item.id);
      }
      allFirstLevelItems.push(item);
    }
  });

  // Now we create the final list of options
  const finalList: Option[] = [];
  allFirstLevelItems.forEach((item) => {
    const group = createdGroups.get(item.id);
    const hasChildren =
      group?.children !== undefined && group.children.length > 0;
    finalList.push(getOption(item, hasChildren));
    if (hasChildren) {
      group.children?.forEach((child) => {
        const childGroup = createdGroups.get(child.id);
        const childHasChildren =
          childGroup?.children !== undefined && childGroup.children.length > 0;
        assert(!childHasChildren, "Only one level of grouping is supported");
        finalList.push(getOption(child, false));
      });
    }
  });

  return finalList;
}
